/**
 * Synthetic 8,760-hour home load engine for Texas (DESIGN §4.1).
 *
 * Ported from ES `src/load/synthetic.ts` + `src/load/profiler.ts`
 * (decompose → weather-correct → calibrate). Texas changes:
 * - CA climate-zone intensities are replaced by region cooling/heating
 *   intensities passed in by the caller (from `src/config/regions.ts`).
 * - EULP/R2 and the isolate cache are dropped (synthetic only, pure functions).
 * - Calibration returns calibrated *components*, so the Nest cooling reduction
 *   can be applied after calibration (the "today" load still matches the bill).
 * - EV charging is a separate vector `ev(h)` (see {@link buildEvProfile}).
 * - Float64Array is used throughout so bills reconcile to the cent.
 *
 * Pure: no DOM, network or global state.
 */
import type { LoadArchetype } from "../config/types";
import {
  HOURS_IN_YEAR,
  MONTH_HOURS,
  MONTH_START_HOUR,
  getHourOfDay,
  getMonthFromHour,
  isWeekend,
  sumByMonth,
  sumVector,
} from "./calendar";

/** Climate parameters the load engine needs (subset of `RegionConfig`). */
export interface LoadClimate {
  readonly coolingIntensity: number;
  readonly heatingIntensity: number;
}

// ── ES shapes (copied verbatim) ─────────────────────────────────────────────

/** Month cooling weights (Jan=0 … Dec=11). */
export const MONTH_COOLING_WEIGHTS = [0.0, 0.0, 0.02, 0.08, 0.3, 0.8, 1.0, 1.0, 0.75, 0.25, 0.04, 0.0] as const;

/** Month heating weights (Jan=0 … Dec=11). */
export const MONTH_HEATING_WEIGHTS = [1.0, 0.85, 0.65, 0.25, 0.05, 0.0, 0.0, 0.0, 0.0, 0.18, 0.65, 0.95] as const;

/** Diurnal baseload shape, weekdays. */
export const BASELOAD_WEEKDAY = [
  0.32, 0.3, 0.28, 0.28, 0.3, 0.38, 0.6, 0.9, 0.85, 0.65, 0.55, 0.52, 0.5, 0.48, 0.5, 0.55, 0.7, 0.95, 1.2, 1.25,
  1.1, 0.85, 0.55, 0.4,
] as const;

/** Diurnal baseload shape, weekends. */
export const BASELOAD_WEEKEND = [
  0.35, 0.32, 0.3, 0.3, 0.32, 0.35, 0.42, 0.55, 0.75, 0.85, 0.82, 0.8, 0.78, 0.75, 0.74, 0.75, 0.82, 0.95, 1.1, 1.15,
  1.05, 0.85, 0.6, 0.45,
] as const;

/** Diurnal cooling curve (peaks 14:00–19:00). */
export const COOLING_DIURNAL = [
  0.1, 0.06, 0.04, 0.02, 0.02, 0.04, 0.06, 0.1, 0.18, 0.3, 0.55, 0.75, 0.95, 1.15, 1.35, 1.5, 1.55, 1.4, 1.2, 0.95,
  0.7, 0.48, 0.3, 0.18,
] as const;

/** Diurnal heating curve (morning and evening peaks). */
export const HEATING_DIURNAL = [
  0.65, 0.6, 0.6, 0.62, 0.68, 0.85, 1.25, 1.4, 1.2, 0.85, 0.6, 0.45, 0.35, 0.3, 0.3, 0.35, 0.5, 0.85, 1.15, 1.3,
  1.25, 1.1, 0.9, 0.75,
] as const;

/** electric_heat has ~3.7× the winter space-heating electricity of gas_heat (ES). */
export const HEATING_ARCHETYPE_MULTIPLIER: Readonly<Record<LoadArchetype, number>> = {
  electric_heat: 1.18,
  gas_heat: 0.32,
};

export function getBaseloadWeight(hourOfDay: number, weekend: boolean): number {
  return weekend ? BASELOAD_WEEKEND[hourOfDay] : BASELOAD_WEEKDAY[hourOfDay];
}

export function getCoolingWeight(month: number, hourOfDay: number, climate: LoadClimate): number {
  return MONTH_COOLING_WEIGHTS[month] * climate.coolingIntensity * COOLING_DIURNAL[hourOfDay] * 0.85;
}

export function getHeatingWeight(
  month: number,
  hourOfDay: number,
  climate: LoadClimate,
  archetype: LoadArchetype,
): number {
  return (
    MONTH_HEATING_WEIGHTS[month] *
    climate.heatingIntensity *
    HEATING_ARCHETYPE_MULTIPLIER[archetype] *
    HEATING_DIURNAL[hourOfDay] *
    0.65
  );
}

/** Uncalibrated synthetic base profile (relative kW shape). */
export function generateSyntheticBaseProfile(climate: LoadClimate, archetype: LoadArchetype): Float64Array {
  const profile = new Float64Array(HOURS_IN_YEAR);
  for (let h = 0; h < HOURS_IN_YEAR; h++) {
    const month = getMonthFromHour(h);
    const hod = getHourOfDay(h);
    const b = getBaseloadWeight(hod, isWeekend(h));
    const c = getCoolingWeight(month, hod, climate);
    const ht = getHeatingWeight(month, hod, climate, archetype);
    profile[h] = Math.max(0.05, b + c + ht);
  }
  return profile;
}

// ── Decomposition / weather correction ─────────────────────────────────────

/** Baseload / heating / cooling components; they sum to the total each hour. */
export interface LoadComponents {
  readonly baseload: Float64Array;
  readonly heating: Float64Array;
  readonly cooling: Float64Array;
}

/**
 * Splits a profile into baseload, heating and cooling by the shape weights.
 * Invariant: baseload + heating + cooling === profile (exact by construction).
 */
export function decomposeProfile(
  profile: ArrayLike<number>,
  climate: LoadClimate,
  archetype: LoadArchetype,
): LoadComponents {
  const baseload = new Float64Array(HOURS_IN_YEAR);
  const heating = new Float64Array(HOURS_IN_YEAR);
  const cooling = new Float64Array(HOURS_IN_YEAR);
  for (let h = 0; h < HOURS_IN_YEAR; h++) {
    const total = profile[h];
    const month = getMonthFromHour(h);
    const hod = getHourOfDay(h);
    const bW = getBaseloadWeight(hod, isWeekend(h));
    const cW = getCoolingWeight(month, hod, climate);
    const hW = getHeatingWeight(month, hod, climate, archetype);
    const tW = bW + cW + hW;
    if (tW <= 0 || total <= 0) {
      baseload[h] = total;
    } else {
      baseload[h] = total * (bW / tW);
      heating[h] = total * (hW / tW);
      cooling[h] = total - (baseload[h] + heating[h]);
    }
  }
  return { baseload, heating, cooling };
}

/** Optional degree-day weather normalisation (ES). Ratios clamp to [0.2, 3.0]. */
export interface WeatherFactors {
  cddActual?: number;
  cddNormal?: number;
  hddActual?: number;
  hddNormal?: number;
}

function ddRatio(actual?: number, normal?: number): number {
  if (actual === undefined || normal === undefined || normal < 10) return 1;
  return Math.max(0.2, Math.min(3.0, actual / normal));
}

/** Scales the HVAC components by degree-day ratios (ES `weatherCorrectHvac`). */
export function weatherCorrect(c: LoadComponents, factors?: WeatherFactors): LoadComponents {
  const cdd = ddRatio(factors?.cddActual, factors?.cddNormal);
  const hdd = ddRatio(factors?.hddActual, factors?.hddNormal);
  if (cdd === 1 && hdd === 1) return c;
  const heating = new Float64Array(HOURS_IN_YEAR);
  const cooling = new Float64Array(HOURS_IN_YEAR);
  for (let h = 0; h < HOURS_IN_YEAR; h++) {
    heating[h] = c.heating[h] * hdd;
    cooling[h] = c.cooling[h] * cdd;
  }
  return { baseload: c.baseload, heating, cooling };
}

// ── Calibration ─────────────────────────────────────────────────────────────

/**
 * Customer usage input. Three ES modes plus "average monthly":
 * - `monthly`: 12 monthly kWh (bill history / Smart Meter Texas). Index 0 = January.
 * - `annual`: annual kWh.
 * - `averageMonthly`: average monthly kWh, treated as annual = avg × 12.
 */
export type UsageInput =
  | { readonly mode: "monthly"; readonly monthlyKwh: readonly number[] }
  | { readonly mode: "annual"; readonly annualKwh: number }
  | { readonly mode: "averageMonthly"; readonly averageMonthlyKwh: number };

function assertNonNegative(n: number, label: string): void {
  if (!Number.isFinite(n) || n < 0) throw new Error(`${label} must be a non-negative number, got ${n}`);
}

function scaleComponents(c: LoadComponents, scaleForHour: (h: number) => number): LoadComponents {
  const baseload = new Float64Array(HOURS_IN_YEAR);
  const heating = new Float64Array(HOURS_IN_YEAR);
  const cooling = new Float64Array(HOURS_IN_YEAR);
  for (let h = 0; h < HOURS_IN_YEAR; h++) {
    const s = scaleForHour(h);
    baseload[h] = c.baseload[h] * s;
    heating[h] = c.heating[h] * s;
    cooling[h] = c.cooling[h] * s;
  }
  return { baseload, heating, cooling };
}

/** Sum of the three components per hour. */
export function composeLoad(c: LoadComponents, coolingFactor = 1): Float64Array {
  const out = new Float64Array(HOURS_IN_YEAR);
  for (let h = 0; h < HOURS_IN_YEAR; h++) out[h] = c.baseload[h] + c.heating[h] + c.cooling[h] * coolingFactor;
  return out;
}

/**
 * Calibrates components to 12 monthly kWh targets (ES `calibrateMonthly`).
 * A zero target zeroes the month; an all-zero month shape falls back to uniform
 * (assigned to baseload).
 */
export function calibrateMonthly(c: LoadComponents, monthlyKwh: readonly number[]): LoadComponents {
  if (monthlyKwh.length !== 12) {
    throw new Error(`monthlyKwh must contain exactly 12 values, got ${monthlyKwh.length}`);
  }
  monthlyKwh.forEach((v, i) => assertNonNegative(v, `monthlyKwh[${i}]`));
  const sums = sumByMonth(composeLoad(c));
  const out = scaleComponents(c, (h) => {
    const m = getMonthFromHour(h);
    return sums[m] > 0 ? monthlyKwh[m] / sums[m] : 0;
  });
  for (let m = 0; m < 12; m++) {
    if (sums[m] <= 0 && monthlyKwh[m] > 0) {
      const uniform = monthlyKwh[m] / MONTH_HOURS[m];
      for (let h = MONTH_START_HOUR[m]; h < MONTH_START_HOUR[m] + MONTH_HOURS[m]; h++) out.baseload[h] = uniform;
    }
  }
  return out;
}

/** Calibrates components to an annual kWh target (ES `calibrateAnnual`). */
export function calibrateAnnual(c: LoadComponents, annualKwh: number): LoadComponents {
  assertNonNegative(annualKwh, "annualKwh");
  const total = sumVector(composeLoad(c));
  if (total <= 0) {
    const out = scaleComponents(c, () => 0);
    out.baseload.fill(annualKwh / HOURS_IN_YEAR);
    return out;
  }
  const s = annualKwh / total;
  return scaleComponents(c, () => s);
}

/** Calibrates components to any {@link UsageInput}. */
export function calibrate(c: LoadComponents, usage: UsageInput): LoadComponents {
  switch (usage.mode) {
    case "monthly":
      return calibrateMonthly(c, usage.monthlyKwh);
    case "annual":
      return calibrateAnnual(c, usage.annualKwh);
    case "averageMonthly":
      assertNonNegative(usage.averageMonthlyKwh, "averageMonthlyKwh");
      return calibrateAnnual(c, usage.averageMonthlyKwh * 12);
  }
}

/**
 * Removes EV energy that is already inside the usage history, month by month,
 * by scaling all three components down proportionally. Used for the
 * "EV already in usage history" mode so the EV kWh can be re-added as a
 * separate overnight `ev(h)` vector without double counting.
 * A month never goes below zero.
 */
export function carveOutMonthlyKwh(c: LoadComponents, monthlyKwhToRemove: readonly number[]): LoadComponents {
  if (monthlyKwhToRemove.length !== 12) throw new Error("monthlyKwhToRemove must have 12 values");
  const sums = sumByMonth(composeLoad(c));
  return scaleComponents(c, (h) => {
    const m = getMonthFromHour(h);
    if (sums[m] <= 0) return 0;
    return Math.max(0, sums[m] - monthlyKwhToRemove[m]) / sums[m];
  });
}

// ── Orchestration ───────────────────────────────────────────────────────────

export interface HomeLoadOptions {
  readonly climate: LoadClimate;
  readonly archetype: LoadArchetype;
  readonly usage: UsageInput;
  readonly weather?: WeatherFactors;
}

export interface HomeLoadResult {
  /** Calibrated components; `composeLoad(components)` reproduces the usage input. */
  readonly components: LoadComponents;
  /** Calibrated total home load (no Nest), kWh per hour. */
  readonly hourly: Float64Array;
  readonly monthlyKwh: number[];
  readonly annualKwh: number;
}

/** Full pipeline: synthetic base → decompose → weather-correct → calibrate. */
export function generateHomeLoad(opts: HomeLoadOptions): HomeLoadResult {
  const base = generateSyntheticBaseProfile(opts.climate, opts.archetype);
  const decomposed = decomposeProfile(base, opts.climate, opts.archetype);
  const corrected = weatherCorrect(decomposed, opts.weather);
  const components = calibrate(corrected, opts.usage);
  const hourly = composeLoad(components);
  const monthlyKwh = sumByMonth(hourly);
  return { components, hourly, monthlyKwh, annualKwh: monthlyKwh.reduce((a, b) => a + b, 0) };
}

/**
 * Applies the 🌡️ Nest cooling reduction after calibration:
 * `cooling'(h) = cooling(h) × (1 − pct)`. Baseload and heating are unchanged.
 */
export function applyNestCoolingReduction(c: LoadComponents, pct: number): Float64Array {
  if (!Number.isFinite(pct) || pct < 0 || pct > 1) throw new Error(`Nest reduction must be in [0, 1], got ${pct}`);
  return composeLoad(c, 1 - pct);
}

// ── EV ─────────────────────────────────────────────────────────────────────

export interface EvProfileOptions {
  readonly annualMiles: number;
  readonly kWhPerMile: number;
  /** Charging window [startHour, endHour) in hour-of-day, e.g. [0, 5]. */
  readonly window: readonly [number, number];
}

/** EV kWh per year for the options. */
export function evAnnualKwh(o: Pick<EvProfileOptions, "annualMiles" | "kWhPerMile">): number {
  return o.annualMiles * o.kWhPerMile;
}

/**
 * Overnight smart-charge vector: `annualMiles × kWhPerMile / 365` kWh per day,
 * spread evenly across the window hours.
 */
export function buildEvProfile(o: EvProfileOptions): Float64Array {
  assertNonNegative(o.annualMiles, "annualMiles");
  assertNonNegative(o.kWhPerMile, "kWhPerMile");
  const [start, end] = o.window;
  if (!(Number.isInteger(start) && Number.isInteger(end) && start >= 0 && end <= 24 && end > start)) {
    throw new Error(`Invalid EV window [${start}, ${end})`);
  }
  const perHour = evAnnualKwh(o) / 365 / (end - start);
  const ev = new Float64Array(HOURS_IN_YEAR);
  for (let h = 0; h < HOURS_IN_YEAR; h++) {
    const hod = getHourOfDay(h);
    if (hod >= start && hod < end) ev[h] = perHour;
  }
  return ev;
}
